<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <v-btn icon @click="callBackRoute" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6">Emergency Order Containers</span>
      <v-icon small class="mx-1">mdi-chevron-right</v-icon>
      <span class="text-h6 primary--text mr-3">{{
        container.container_number
      }}</span>
      <v-chip
        small
        label
        :color="statusColor"
        text-color="white"
        class="font-weight-bold"
      >
        {{ container.status }}
      </v-chip>
    </div>

    <div
      v-if="container.failure_reason"
      class="failure-banner mb-3"
    >
      <div class="failure-banner__title">
        <v-icon color="#b45309" size="16">mdi-alert-circle-outline</v-icon>
        Validation Failures
      </div>
      <ul class="failure-banner__list">
        <li v-for="(reason, i) in failureReasonList" :key="i">{{ reason }}</li>
      </ul>
    </div>

    <div
      v-if="container.draft_subhire"
      class="info-banner mb-3"
    >
      <v-icon color="#1d4ed8" size="16">mdi-information-outline</v-icon>
      <span>
        Driver and Vehicle are not in the system, so this bonded-route sub-hire container's
        DA/Leg were created directly and a <strong>Draft Sub-hire</strong> was raised. It will
        be consolidated once a matching vendor bulk upload is approved.
      </span>
    </div>

    <v-card flat outlined class="rounded mb-3">
      <div class="section-head">Order Details</div>

      <div class="route-strip">
        <div class="route-strip__lane">
          <div class="field-label">Lane</div>
          <div class="field-value">{{ container.lane_title || "-" }}</div>
        </div>
        <div class="route-strip__path">
          <div class="field-label">Origin</div>
          <div v-if="container.origin_name" class="field-value field-value--accent">{{ container.origin_name }}</div>
          <div v-else-if="container.origin_raw" class="field-value field-value--unresolved" :title="`'${container.origin_raw}' not found in the system`">
            {{ container.origin_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
        <v-icon size="16" color="#9e9e9e">mdi-arrow-right</v-icon>
        <div class="route-strip__path">
          <div class="field-label">Destination</div>
          <div v-if="container.destination_name" class="field-value field-value--accent">{{ container.destination_name }}</div>
          <div v-else-if="container.destination_raw" class="field-value field-value--unresolved" :title="`'${container.destination_raw}' not found in the system`">
            {{ container.destination_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
      </div>

      <div class="field-grid">
        <div class="field-cell">
          <div class="field-label">Reference</div>
          <div class="field-value">{{ container.vbs_reference_no || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Voyage ID</div>
          <div class="field-value">{{ container.voyage_id || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Sub Hire</div>
          <div class="field-value">{{ container.is_subhire ? "Yes" : "No" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Customer</div>
          <div class="field-value">{{ container.customer_name || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Vendor</div>
          <div v-if="container.vendor_name" class="field-value">{{ container.vendor_name }}</div>
          <div v-else-if="container.vendor_name_raw" class="field-value field-value--unresolved" :title="`'${container.vendor_name_raw}' not found in the system`">
            {{ container.vendor_name_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
        <div class="field-cell">
          <div class="field-label">DA Rate</div>
          <div class="field-value">{{ container.rate ? `AED ${container.rate}` : "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Sub Hire Rate</div>
          <div class="field-value">{{ container.subhire_rate ? `AED ${container.subhire_rate}` : "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Vehicle No (CODECO)</div>
          <div class="field-value field-value--accent">{{ container.vehicle_no || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">System Vehicle</div>
          <div class="field-value field-value--accent">{{ container.vehicle_name || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Driver ID (CODECO)</div>
          <div class="field-value field-value--accent">{{ container.driver_id_raw || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">System Driver</div>
          <div class="field-value field-value--accent">{{ container.driver_name || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label field-label--with-icon">
            <span>Gate Out Terminal</span>
            <v-tooltip v-if="!container.gate_out_terminal" top>
              <template #activator="{ on, attrs }">
                <v-icon x-small color="info" v-bind="attrs" v-on="on">mdi-information</v-icon>
              </template>
              <span>No gate-out event has been received for this container yet.</span>
            </v-tooltip>
          </div>
          <div class="field-value">{{ container.gate_out_terminal || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Gate In Terminal</div>
          <div class="field-value">{{ container.gate_in_terminal || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Movement Date</div>
          <div class="field-value">{{ formatDate(container.movement_date) || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Completed On</div>
          <div class="field-value">{{ formatDate(container.completed_on, true) || "-" }}</div>
        </div>
      </div>
    </v-card>

    <v-card flat outlined class="rounded mb-3">
      <div class="section-head">
        DA / Leg / Trip
        <v-spacer />
        <v-btn
          v-if="!container.da"
          color="primary"
          x-small
          depressed
          @click="showCreateDADialog = true"
        >
          <v-icon x-small class="mr-1">mdi-file-plus-outline</v-icon>
          Create DA
        </v-btn>
      </div>
      <div class="field-grid">
        <div class="field-cell">
          <div class="field-label">Delivery Advice</div>
          <router-link
            v-if="container.da"
            :to="`/app/admin/delivery-advice/details/${container.da}`"
            class="field-value link-cell"
          >
            {{ container.da_no }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">-</div>
          <v-chip
            v-if="container.da_status"
            x-small
            label
            :color="statusChipColor(container.da_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ container.da_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label">Leg</div>
          <div class="field-value">{{ container.leg_display_id || "-" }}</div>
          <v-chip
            v-if="container.leg_status"
            x-small
            label
            :color="statusChipColor(container.leg_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ container.leg_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label field-label--with-icon">
            <span>Trip</span>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon x-small color="info" v-bind="attrs" v-on="on">mdi-information</v-icon>
              </template>
              <span>Trip is marked Completed once all its legs' drop arrivals are done.</span>
            </v-tooltip>
          </div>
          <router-link
            v-if="container.trip"
            :to="`/app/admin/draft-trip/details/${container.trip}`"
            class="field-value link-cell"
          >
            {{ container.trip_id }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">-</div>
          <v-chip
            v-if="container.trip_status"
            x-small
            label
            :color="statusChipColor(container.trip_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ container.trip_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label">Sub Hire Job</div>
          <router-link
            v-if="container.subhire_job"
            :to="`/app/admin/sub-hire/details/${container.subhire_job}`"
            class="field-value link-cell"
          >
            {{ container.subhire_job_no }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">N/A</div>
          <v-chip
            v-if="container.subhire_job_status"
            x-small
            label
            :color="statusChipColor(container.subhire_job_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ container.subhire_job_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label">{{ container.draft_subhire ? "Draft Sub-hire" : "Draft DA" }}</div>
          <router-link
            v-if="container.draft_da"
            :to="`/app/admin/draft-da/details/${container.draft_da}`"
            class="field-value link-cell"
          >
            {{ container.draft_da_no }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <router-link
            v-else-if="container.draft_subhire"
            :to="`/app/admin/draft-subhire/list?search=${container.draft_subhire_no}`"
            class="field-value link-cell"
          >
            {{ container.draft_subhire_no }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">N/A</div>
          <v-chip
            v-if="container.draft_da_status || container.draft_subhire_status"
            x-small
            label
            :color="draftDaStatusColor(container.draft_da_status || container.draft_subhire_status)"
            text-color="white"
            class="font-weight-bold mt-1 text-capitalize"
          >
            {{ container.draft_da_status || container.draft_subhire_status }}
          </v-chip>
        </div>
      </div>
    </v-card>

    <v-row dense>
      <v-col cols="12" md="7">
        <v-card flat outlined class="rounded" height="100%">
          <div class="section-head">
            Attached Container
            <v-spacer />
            <span class="section-head__meta">1 Unit(s)</span>
          </div>
          <div class="pa-4">
            <div class="d-flex align-center mb-3">
              <span class="font-weight-bold primary--text mr-2"
                >#1 {{ container.container_number }}</span
              >
              <v-chip x-small outlined :color="container.container_type_name ? undefined : 'warning'">{{
                container.container_type_name || container.container_type_raw || "-"
              }}</v-chip>
            </div>

            <div class="field-label mb-1">Milestones</div>
            <div class="milestone-list">
              <div
                v-for="milestone in milestones"
                :key="milestone.label"
                class="milestone-row"
              >
                <span
                  class="milestone-row__dot"
                  :class="{ 'milestone-row__dot--done': milestone.value }"
                >
                  <v-icon v-if="milestone.value" size="11" color="white">mdi-check</v-icon>
                </span>
                <span class="milestone-row__label">{{ milestone.label }}</span>
                <span v-if="milestone.value" class="milestone-row__time">{{
                  formatDate(milestone.value, true)
                }}</span>
                <v-chip v-else x-small outlined color="grey" class="ml-2">Pending</v-chip>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card flat outlined class="rounded" height="100%">
          <div class="section-head">
            Activity Log
            <v-spacer />
            <span class="section-head__meta">{{ (container.logs || []).length }} event(s)</span>
          </div>
          <div class="pa-3 activity-log">
            <div
              v-for="log in container.logs"
              :key="log.id"
              class="activity-log__item"
            >
              <div class="d-flex justify-space-between align-baseline">
                <span class="activity-log__author">{{ log.updated_by_name || "System" }}</span>
                <span class="activity-log__time">{{ formatDate(log.created, true) }}</span>
              </div>
              <div class="activity-log__message">{{ log.message }}</div>
            </div>
            <p
              v-if="!container.logs || !container.logs.length"
              class="caption grey--text text-center ma-0 py-2"
            >
              No activity logs yet.
            </p>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <CreateContainerDADialog
      v-if="!container.is_subhire"
      v-model="showCreateDADialog"
      :container="container"
      @daCreated="getContainerDetails"
    />
    <CreateSubhireContainerDADialog
      v-else
      v-model="showCreateDADialog"
      :container="container"
      @daCreated="getContainerDetails"
    />
  </div>
</template>
<script>
import CreateContainerDADialog from "@/components/ModuleBased/dialogForms/CreateContainerDADialog.vue";
import CreateSubhireContainerDADialog from "@/components/ModuleBased/dialogForms/CreateSubhireContainerDADialog.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
  formatDate,
} from "@/utils/functions";

export default {
  components: { CreateContainerDADialog, CreateSubhireContainerDADialog },
  data() {
    return {
      container: {},
      permissions: null,
      showCreateDADialog: false,
    };
  },
  computed: {
    statusColor() {
      const colors = {
        Pending: "grey darken-1",
        Active: "blue darken-1",
        Completed: "green darken-1",
        Cancelled: "red darken-1",
      };
      return colors[this.container.status] || "grey";
    },
    milestones() {
      return [
        { label: "Pickup Departure", value: this.container.pickup_departure },
        { label: "Drop Arrival", value: this.container.drop_arrival },
      ];
    },
    failureReasonList() {
      return (this.container.failure_reason || "")
        .split("\n")
        .map((reason) => reason.trim())
        .filter(Boolean);
    },
  },
  methods: {
    formatDate,
    statusChipColor(status) {
      const colors = {
        Created: "grey darken-1",
        Scheduled: "blue darken-1",
        Assigned: "blue darken-1",
        Active: "orange darken-1",
        Completed: "green darken-1",
        "Ready For Invoice": "teal darken-1",
        "Invoice Generated": "purple darken-1",
        Cancelled: "red darken-1",
      };
      return colors[status] || "grey";
    },
    draftDaStatusColor(status) {
      const colors = {
        open: "amber darken-2",
        confirmed: "green darken-1",
        cancelled: "red darken-1",
      };
      return colors[status] || "grey";
    },
    callBackRoute() {
      this.$router.push("/app/admin/emergency-order-container/list");
    },
    getContainerDetails() {
      this.$bus.$emit("showLoader", true);
      this.$api.emergencyOrderContainer
        .getEmergencyOrderContainerObject(this.$route.params.id)
        .then((res) => {
          this.container = res.data;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.getContainerDetails();
      }
    }
  },
};
</script>
<style scoped>
/* ---- section shell ---- */
.rounded {
  border-radius: 8px !important;
}
.section-head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.section-head__meta {
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.45);
}

/* ---- validation banner ---- */
.failure-banner {
  border: 1px solid #fbbf24;
  background: #fffbeb;
  border-radius: 8px;
  padding: 10px 16px;
}
.failure-banner__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 6px;
}
.failure-banner__list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #78350f;
}
.failure-banner__list li + li {
  margin-top: 2px;
}

/* ---- draft sub-hire info banner ---- */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: #1e3a8a;
}

/* ---- route strip ---- */
.route-strip {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 10px 16px;
  background-color: #fcfcfc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.route-strip__path {
  display: flex;
  flex-direction: column;
}

/* ---- field grid: dense label/value pairs, no card-in-card ---- */
.field-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
}
.field-grid--four {
  grid-template-columns: repeat(4, 1fr);
}
.field-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  min-width: 0;
}
.field-cell .field-value {
  margin-bottom: 2px;
}
.field-cell .v-chip {
  margin-top: auto;
}
.field-cell:nth-child(5n) {
  border-right: none;
}
.field-grid--four .field-cell:nth-child(4n) {
  border-right: none;
}
.field-cell:nth-last-child(-n + 5) {
  border-bottom: none;
}

.field-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 2px;
}
.field-label--with-icon {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1;
}
.field-value {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-value--accent {
  color: var(--v-primary-base, #1976d2);
}
.field-value--unresolved {
  color: #b45309;
  cursor: help;
}
.field-value--unresolved::after {
  content: " (unresolved)";
  font-size: 10px;
  font-weight: 500;
  color: #b45309;
  opacity: 0.75;
}
.link-cell {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

/* ---- milestones ---- */
.milestone-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.milestone-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.milestone-row__dot {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.milestone-row__dot--done {
  background: #2e7d32;
}
.milestone-row__label {
  font-size: 13px;
  font-weight: 600;
  min-width: 110px;
}
.milestone-row__time {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

/* ---- activity log ---- */
.activity-log {
  max-height: 360px;
  overflow-y: auto;
}
.activity-log__item {
  padding: 8px 10px;
  border-radius: 6px;
}
.activity-log__item + .activity-log__item {
  margin-top: 4px;
}
.activity-log__item:nth-child(odd) {
  background: #fafafa;
}
.activity-log__author {
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
}
.activity-log__time {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}
.activity-log__message {
  font-size: 13px;
  margin-top: 2px;
  line-height: 1.4;
}

@media (max-width: 960px) {
  .field-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .field-grid--four {
    grid-template-columns: repeat(2, 1fr);
  }
  .field-cell:nth-child(5n),
  .field-grid--four .field-cell:nth-child(4n) {
    border-right: 1px solid rgba(0, 0, 0, 0.06);
  }
  .field-cell:nth-child(2n) {
    border-right: none;
  }
  .field-cell:nth-last-child(-n + 5) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .field-cell:nth-last-child(-n + 2) {
    border-bottom: none;
  }
}
</style>
